export const PLANNED_MAP_CONTEXT_STORAGE_KEY = "mapMonitoring_plannedMapContext";

/**
 * Только режим «Изменить вручную» из планирования: на карте запрашиваем маршруты с этой датой и algorithm.
 * Обычный заход на карту к этому не привязан — там всегда отдельный GET (см. MapMonitoring).
 */
export type PlannedMapContext = {
    planningPreview: { routeDate: string; algorithm: string } | null;
};

const defaultContext: PlannedMapContext = {
    planningPreview: null,
};

function readRouteDate(p: Record<string, unknown>): string | null {
    const v = p.routeDate ?? p.RouteDate;
    if (typeof v === "string" && v.trim()) return v.trim();
    return null;
}

function readAlgorithm(p: Record<string, unknown>): string | null {
    const v = p.algorithm ?? p.Algorithm;
    if (typeof v === "string" && v.trim()) return v.trim();
    return null;
}

function readShowAll(p: Record<string, unknown>): boolean {
    const v = p.showAllRoutesOnMap ?? p.ShowAllRoutesOnMap;
    return Boolean(v);
}

function readPlanningPreviewNested(p: Record<string, unknown>): { routeDate: string; algorithm: string } | null {
    const raw = p.planningPreview ?? p.PlanningPreview;
    if (!raw || typeof raw !== "object") return null;
    const o = raw as Record<string, unknown>;
    const routeDate = readRouteDate(o);
    if (!routeDate) return null;
    const algorithm = readAlgorithm(o) ?? "";
    return { routeDate, algorithm };
}

/** Совместимость со старым JSON (routeDate + algorithm + showAllRoutesOnMap). */
function migrateLegacy(p: Record<string, unknown>): PlannedMapContext {
    const nested = readPlanningPreviewNested(p);
    if (nested) return { planningPreview: nested };

    if (readShowAll(p)) {
        const routeDate = readRouteDate(p);
        if (routeDate) return { planningPreview: { routeDate, algorithm: readAlgorithm(p) ?? "" } };
    }
    return { planningPreview: null };
}

export function getPlannedMapContext(): PlannedMapContext {
    try {
        const raw = localStorage.getItem(PLANNED_MAP_CONTEXT_STORAGE_KEY);
        if (!raw) return { ...defaultContext };
        const p = JSON.parse(raw) as Record<string, unknown>;
        return migrateLegacy(p);
    } catch {
        return { ...defaultContext };
    }
}

export function savePlannedMapContext(patch: Partial<PlannedMapContext>): PlannedMapContext {
    const next: PlannedMapContext = { ...getPlannedMapContext(), ...patch };
    localStorage.setItem(PLANNED_MAP_CONTEXT_STORAGE_KEY, JSON.stringify(next));
    window.dispatchEvent(new Event("planned-map-context-changed"));
    return next;
}

/** Как в recomputeMapData MapMonitoring: множество выбранных машин или «все выбранные контрагенты целиком». */
export function getVehicleFilterMode(selection: {
    vehicle: Array<{ showAll: boolean; machineIds: string[] }>;
}): "all" | Set<string> {
    for (const v of selection.vehicle) {
        if (v.showAll) return "all";
    }
    const ids = selection.vehicle.flatMap((x) => x.machineIds ?? []);
    if (ids.length === 0) return "all";
    return new Set(ids.map(String));
}
